import { useMemo } from 'react';

import { useAppSelector } from '@/hooks/useAppSelector';

import type { ScenarioMode } from '../stores/scenarioSlice';

export interface TemporalDisplayContext {
  /** Redux scenario mode */
  mode: ScenarioMode;
  /** Live + năm phân tích = năm dương lịch hiện tại — copy dự báo ngắn hạn ở sidebar/modal (không còn panel 48h trên bản đồ). */
  allowsForecast: boolean;
  /**
   * Thanh thời gian 24h và tab lịch sử điểm ngập — chỉ khi xem lại năm cũ / không phải ngữ cảnh “live + năm hiện tại”.
   */
  allowsHistoryTimeline: boolean;
  /** Year used for historical playback / GEE (from map or scenario) */
  displayYear: number | null;
  /** ISO date string when user selected a moment in time */
  selectedDateIso: string | null;
  /** Calendar year used for analysis (GEE / scenario), with fallback to current year */
  analysisYear: number;
}

/**
 * Centralizes rules for time-based UI.
 * - `allowsForecast`: `live` + năm phân tích = năm hiện tại — sidebar/modal dùng ngữ cảnh dự báo ngắn hạn.
 * - `allowsHistoryTimeline`: luôn ngược với `allowsForecast` — thanh 24h + lịch sử điểm ngập khi xem năm cũ hoặc historical/regional.
 *
 * `analysisYear` = `scenario.selectedYear ?? map.geeParams.year` (khớp dropdown “Năm …” trên header).
 */
export function useTemporalDisplayContext(): TemporalDisplayContext {
  const mode = useAppSelector((s) => s.scenario.mode);
  const selectedYear = useAppSelector((s) => s.scenario.selectedYear);
  const selectedDate = useAppSelector((s) => s.scenario.selectedDate);
  const geeYear = useAppSelector((s) => s.map.geeParams.year);

  return useMemo(() => {
    const currentCalendarYear = new Date().getFullYear();
    const analysisYear = selectedYear ?? geeYear ?? currentCalendarYear;
    const displayYear = selectedYear ?? geeYear ?? null;
    const allowsForecast = mode === 'live' && analysisYear === currentCalendarYear;
    const allowsHistoryTimeline = !allowsForecast;

    return {
      mode,
      allowsForecast,
      allowsHistoryTimeline,
      displayYear,
      analysisYear,
      selectedDateIso: selectedDate,
    };
  }, [mode, selectedYear, geeYear, selectedDate]);
}
